import { withTransaction } from '@elastic/apm-rum-react'
import {
  Button,
  Callout,
  CircleCompletation,
  CommunicationMediaKit,
  DakenService,
  Environment,
  HeimdallService,
  InsideNavigationParcelComponent,
  Loader,
  LogService,
  MediaKit,
  MenuItemNavigation,
  NavigationLayout,
  onEnvironmentChange,
  onUserChange,
  ToastHelper,
  User,
  UserHelper,
  WhoDoesMediaKit,
} from '@hub/shared'
import { Purpose } from '@hub/shared/interfaces/mediakit.interface'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import './detail-mediakit.page.scoped.scss'
import './detail-mediakit.page.scss'
import AboutPage from './pages/about/about.page'
import AnalyticsPage from './pages/analytics/analytics.page'
import BrandsPage from './pages/brands/brands.page'
import CommunicationPage from './pages/communication/communication.page'
import CoverPage from './pages/cover/cover.page'
import WhoDoesPage from './pages/who-does/who-does.page'

const MediakitDetailPageComponent = (props: any) => {
  const { idProfile } = useParams<{ idProfile: string }>()
  const userHelper = useMemo(() => new UserHelper(), [])
  const [mediakit, setMediakit] = useState<MediaKit | null>(null)
  const dakenService = useMemo(() => new DakenService(), [])
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const environmentChange = useRef<SubscriptionLike | undefined>(undefined)
  const logService = useMemo(() => new LogService(), [])
  const heimdallService = useMemo(() => new HeimdallService(), [])
  const [t] = useTranslation('detailMediaKitPage')
  const [user, setUser] = useState<User | null>(null)
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false)
  const [valid, setValid] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [mediakitCompleteness, setMediakitCompleteness] = useState<number>(0)
  const [mediakitCompletenessDetails, setMediakitCompletenessDetails] = useState<any>(null)
  const completenessDetails = useMemo(() => ['frontCover', 'purpose', 'whoDoes', 'communication'], [])
  const [loadingCompleteness, setLoadingCompleteness] = useState<boolean>(false)
  const [mediaKitUrl, setMediaKitUrl] = useState<string>('')
  const navigate = useNavigate()
  const toast = new ToastHelper().toast
  logService.init('Media Kit Detail Page')

  const [menu, setMenu] = useState<Array<MenuItemNavigation>>([
    {
      icon: 'image-polaroid',
      label: t('cover'),
      path: `/mediakit/${idProfile}/cover`,
    },
    {
      icon: 'address-card',
      label: t('about'),
      path: `/mediakit/${idProfile}/purpose`,
    },
    {
      icon: 'user-tag',
      label: t('who'),
      path: `/mediakit/${idProfile}/whoDoes`,
    },
    {
      icon: 'radio-tuner',
      label: t('communication'),
      path: `/mediakit/${idProfile}/communication`,
    },
    {
      icon: 'tags',
      label: t('brands'),
      path: `/mediakit/${idProfile}/brands`,
    },
    {
      icon: 'chart-line',
      label: t('analytics'),
      path: `/mediakit/${idProfile}/analytics`,
    },
  ])

  const homeNavigationPath = '/mediakit/' + idProfile

  const findTitle = (path: string) => {
    return menu.find((item) => item?.path?.endsWith(path.split('/')[3]))?.label
  }

  useEffect(() => {
    environmentChange.current = onEnvironmentChange.pipe().subscribe((environment: Environment) => {
      setMediaKitUrl(environment?.squidProducts?.mediaKit)
    })

    return () => environmentChange?.current?.unsubscribe()
  }, [])

  const parseLabelKey = (menuItem: MenuItemNavigation) => {
    if (menuItem?.path?.includes('cover')) {
      return 'cover'
    }
    if (menuItem?.path?.includes('purpose')) {
      return 'about'
    }
    if (menuItem?.path?.includes('whoDoes')) {
      return 'who'
    }
    if (menuItem?.path?.includes('communication')) {
      return 'communication'
    }
    if (menuItem?.path?.includes('brands')) {
      return 'brands'
    }
    if (menuItem?.path?.includes('analytics')) {
      return 'analytics'
    }
    return ''
  }

  const parseCompleteness = (menuItem: MenuItemNavigation) => {
    if (menuItem?.path?.includes('cover')) {
      return 'frontCover'
    }
    if (menuItem?.path?.includes('communication')) {
      return 'communication'
    }
    if (menuItem?.path?.includes('purpose')) {
      return 'purpose'
    }
    if (menuItem?.path?.includes('whoDoes')) {
      return 'whoDoes'
    }
    return ''
  }

  const getValidation = (validated: boolean) => {
    setValid(validated)
  }

  const updateCompleteness = useCallback(
    async (thisUser?: User) => {
      if (!loadingCompleteness) {
        try {
          const userChanged = thisUser || user
          setLoadingCompleteness(true)
          const completeness: any = await dakenService.getInflucencersCompleteness(userChanged?.squidId!, 'profile-mediakit')
          const completenessMediakit = completeness.detail.find((i: any) => i.profileId === idProfile)
          setMediakitCompleteness(completenessMediakit?.percentage)
          setMediakitCompletenessDetails(completenessMediakit?.detail)
          setMenu(
            menu?.map((item) => {
              const key = parseCompleteness(item)
              return {
                ...item,
                label: t(parseLabelKey(item)),
                warning: key ? !completenessMediakit.detail[key] : false,
              }
            })
          )
          return true
        } catch (error) {
          logService.error({
            message: 'Error getting completeness',
            error,
          })
          return false
        } finally {
          setLoadingCompleteness(false)
        }
      }
    },
    [loadingCompleteness, user, menu, dakenService, idProfile, logService, t]
  )

  const redirect = useCallback(() => {
    for (const detail of completenessDetails) {
      if (mediakitCompletenessDetails?.[detail] === false) {
        navigate(`/mediakit/${idProfile}/${detail === 'frontCover' ? 'cover' : detail}`)
        break
      }
    }
  }, [completenessDetails, mediakitCompletenessDetails, idProfile, navigate])

  const submit = useCallback(async () => {
    if (!loadingSubmit) {
      setLoadingSubmit(true)
      try {
        const response = await heimdallService.patchMediaKit(mediakit!)
        if (response?.data?.error) {
          throw new Error(response.data.error)
        }
        toast.success(t('success'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
        const instagramUpdatedIndex = user?.instagram?.findIndex((instagram) => instagram.id === idProfile)
        user?.instagram?.splice(instagramUpdatedIndex!, 1, { ...user?.instagram[instagramUpdatedIndex!], hasMediaKit: 1 })
        await userHelper.setUser(user!)
        await updateCompleteness().then((e) => {
          if (e) {
            redirect()
          }
        })
      } catch (error) {
        logService.error({
          message: 'Error submitting mediakit',
          error,
        })
        toast.error(t('errors.unexpected'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      } finally {
        setLoadingSubmit(false)
      }
    }
  }, [loadingSubmit, mediakit, updateCompleteness, heimdallService, logService, t, toast, idProfile, userHelper, user, redirect])

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (userChanged: User) => {
      if (userChanged?.squidId) {
        setUser(userChanged)
        const mediaKitResponse = await heimdallService.getMediaKitSelf(idProfile)
        if (mediaKitResponse?._id) {
          setMediakit(mediaKitResponse)
        } else if (userChanged?.instagram?.find((profile) => profile.id === idProfile)) {
          setMediakit({ profileId: idProfile, enabled: true })
        } else {
          history.pushState({}, '', '/error/not-found')
        }

        await updateCompleteness(userChanged)
        setLoading(false)
      }
    })
    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ToHome = () => {
    return window?.innerWidth > 991 ? <Navigate to={`/mediakit/${idProfile}/cover`} /> : null
  }

  if (loading) {
    return (
      <div className="media-kit-detail loader-wrapper">
        <Loader size="big" />
      </div>
    )
  }

  return (
    <NavigationLayout
      isHome={location.pathname === `/mediakit/${idProfile}`}
      menu={menu}
      title={t('title')}
      backButton
      onClickBackButton={() => navigate('/mediakit')}
      header={
        <div className="wrapper-header-navigation-mediakit">
          <CircleCompletation value={mediakitCompleteness} />
          {mediakitCompleteness < 100 ? (
            <Callout className="callout-menu-mediakit" title={t('callout.title')}>
              {t('callout.description')}
            </Callout>
          ) : null}
        </div>
      }
      footer={
        <div className="wrapper-footer-navigation-mediakit">
          <a href={`${mediaKitUrl}/${mediakit?.username}?preview=true`} target="_blank">
            <Button width="100%" size="sm" color="var(--primary_color)" borderColor="var(--primary_color)" textColor="var(--white)" rounded>
              <span>
                <i className="mr-2 fa-regular fa-up-right-from-square"></i>
                {t('access')}
              </span>
            </Button>
          </a>
          <div className="mt-2 display-flex justify-content-center">
            <p style={{ color: 'var(--gray)', fontSize: '0.85rem' }}>{t('accessMessage')}</p>
          </div>
        </div>
      }
    >
      <InsideNavigationParcelComponent
        homeNavigationPath={homeNavigationPath}
        title={findTitle(location.pathname)}
        footer={!(location.pathname === `/mediakit/${idProfile}/analytics` && window.innerWidth > 991)}
        showOnlyBackButton={location.pathname === `/mediakit/${idProfile}/analytics`}
        loadingButton={loadingSubmit}
        disabledButton={!valid}
        confirmFunction={() => submit()}
      >
        <Routes>
          <Route path={'/'} element={<ToHome />} />
          <Route
            path="/cover"
            element={
              <CoverPage
                {...props}
                getValidation={getValidation}
                name={mediakit?.name}
                loadingSubmit={loadingSubmit}
                onChange={(newName: string) => setMediakit(new MediaKit({ ...mediakit, name: newName }))}
              />
            }
          />
          <Route
            path="/purpose"
            element={
              <AboutPage
                {...props}
                getValidation={getValidation}
                purpose={mediakit?.purpose}
                idProfile={idProfile}
                loadingSubmit={loadingSubmit}
                onChange={(newPurpose: Purpose) => setMediakit(new MediaKit({ ...mediakit, purpose: newPurpose }))}
              />
            }
          />
          <Route
            path="/whoDoes"
            element={
              <WhoDoesPage
                {...props}
                getValidation={getValidation}
                whoDoes={mediakit?.whoDoes}
                idProfile={idProfile}
                userName={mediakit?.username}
                loadingSubmit={loadingSubmit}
                onChange={(newWhoDoes: WhoDoesMediaKit) => setMediakit(new MediaKit({ ...mediakit, whoDoes: newWhoDoes }))}
              />
            }
          />
          <Route
            path="/communication"
            element={
              <CommunicationPage
                {...props}
                communication={mediakit?.communication}
                idProfile={idProfile}
                onChange={(newCommunication: CommunicationMediaKit) => setMediakit({ ...mediakit, communication: newCommunication })}
                getValidation={getValidation}
              />
            }
          />
          <Route
            path="/brands"
            element={
              <BrandsPage
                mediakit={mediakit}
                idProfile={idProfile}
                onChange={(mediakit: MediaKit) => {
                  setMediakit((prevMediaKit) => new MediaKit({ ...prevMediaKit, ...mediakit }))
                }}
                getValidation={getValidation}
              />
            }
          />

          <Route path="/analytics" element={<AnalyticsPage idProfile={idProfile} />} />
          <Route element={<Navigate to="/error/not-found" />} />
        </Routes>
      </InsideNavigationParcelComponent>
    </NavigationLayout>
  )
}

export default withTransaction('MediakitDetailPageComponent', 'component')(MediakitDetailPageComponent)
